import { NextRequest, NextResponse } from "next/server";
import { STABLE_ID_COOKIE_NAME } from "./constants";

/** Read the stable-id cookie from the request (server component / action). */
export async function getStableId(): Promise<string | undefined> {
  const { cookies } = await import("next/headers");
  const cookieStore = await cookies();
  return cookieStore.get(STABLE_ID_COOKIE_NAME)?.value;
}

/**
 * Resolve the stable ID from the request: cookie → URL param → random UUID.
 * Also sets the cookie on the request object so downstream code (e.g. flags)
 * can read it from `req.cookies`.
 */
export function resolveStableId(req: NextRequest): {
  stableId: string;
  isNew: boolean;
} {
  const existing = req.cookies.get(STABLE_ID_COOKIE_NAME)?.value;
  if (existing) {
    return { stableId: existing, isNew: false };
  }

  const stableIdFromUrl = req.nextUrl.searchParams.get("stableId");
  if (!stableIdFromUrl) {
    console.warn(
      "No stableId provided in URL; generating random stable-id: ",
      req.nextUrl.toString()
    );
  }

  let stableId = stableIdFromUrl ?? crypto.randomUUID();

  const isPlaywright = req.headers.get("x-playwright") === "true";
  if (isPlaywright && !stableId.endsWith("+playwright")) {
    stableId = stableId + "+playwright";
  }

  req.cookies.set(STABLE_ID_COOKIE_NAME, stableId);
  return { stableId, isNew: true };
}

/** Persist the stable-id cookie on the outgoing response (1-year expiry). */
export function setStableIdCookie(res: NextResponse, stableId: string): void {
  res.cookies.set(STABLE_ID_COOKIE_NAME, stableId, {
    maxAge: 60 * 60 * 24 * 365,
    httpOnly: true,
    sameSite: "lax",
  });
}
